import { cast, Instance, SnapshotIn, types } from 'mobx-state-tree';
import { buildMessagePreview, buildSenderName } from '../utils';
import { IHouseChatPreview } from '../types';
import { ChatConnectionStatus } from 'http-requests/house-chats';

import { ChatMessageModel } from './message';

const ChatLastMessageSenderModel = types.model('ChatLastMessageSenderModel', {
  first_name: types.optional(types.maybeNull(types.string), null),
  last_name: types.optional(types.maybeNull(types.string), null),
  is_bot: types.optional(types.boolean, false),
});

const ChatLastMessageAttachmentModel = types.model(
  'ChatLastMessageAttachmentModel',
  {
    id: types.optional(types.string, ''),
    type: types.optional(types.string, ''),
  }
);

const ChatLastMessageModel = types.model('ChatLastMessageModel', {
  text: types.optional(types.maybeNull(types.string), null),
  created_at: types.optional(types.string, ''),
  max_message_id: types.optional(types.string, ''),
  is_repost: types.optional(types.boolean, false),
  sender: types.optional(types.maybeNull(ChatLastMessageSenderModel), null),
  system_sender: types.optional(
    types.maybeNull(ChatLastMessageSenderModel),
    null
  ),
  has_attachments: types.optional(types.boolean, false),
  attachments: types.optional(types.array(ChatLastMessageAttachmentModel), []),
  is_deleted: types.optional(types.boolean, false),
});

export const ChatItemModel = types
  .model('ChatItemModel', {
    chat_id: types.optional(types.maybeNull(types.number), null),
    house_ids: types.optional(types.array(types.string), []),
    max_chat_url: types.optional(types.maybeNull(types.string), null),
    chat_title: types.optional(types.maybeNull(types.string), null),
    last_message: types.optional(types.maybeNull(ChatLastMessageModel), null),
    unread_count: types.optional(types.number, 0),
    status: types.optional(
      types.enumeration<ChatConnectionStatus>('ChatConnectionStatus', [
        'connected',
        'disconnected',
        'bot_not_added',
        'bot_not_admin',
      ]),
      'connected'
    ),
  })
  .views((self) => ({
    get senderName(): string | null {
      return buildSenderName(self.last_message?.sender ?? null);
    },
    get preview(): IHouseChatPreview {
      return buildMessagePreview(self.last_message);
    },
  }))
  .actions((self) => ({
    markRead() {
      self.unread_count = 0;
    },
    incrementUnread() {
      self.unread_count += 1;
    },
    setStatus(status: ChatConnectionStatus) {
      self.status = status;
    },
    setLastMessage(message: SnapshotIn<typeof ChatMessageModel>) {
      self.last_message = {
        text: message.text ?? null,
        created_at: message.created_at,
        max_message_id: message.max_message_id,
        // is_repost есть только в last_message из /chats (см. ChatLastMessageDTO) —
        // в самих сообщениях (ChatMessageModel/сокет-события) этого поля нет,
        // так что при live-обновлении через сокет отследить репост нечем
        is_repost: false,
        sender: message.sender
          ? {
              first_name: message.sender.first_name ?? null,
              last_name: message.sender.last_name ?? null,
              is_bot: message.sender.is_bot ?? false,
            }
          : null,
        system_sender: message.system_sender
          ? {
              first_name: message.system_sender.first_name ?? null,
              last_name: message.system_sender.last_name ?? null,
              is_bot: false,
            }
          : null,
        has_attachments: message.has_attachments ?? false,
        attachments: cast(
          (message.attachments ?? []).map((attachment) => ({
            id: attachment.id,
            type: attachment.type,
          }))
        ),
        is_deleted: message.is_deleted ?? false,
      };
    },
  }));

export type TChatItem = Instance<typeof ChatItemModel>;
